import { useCallback, useEffect, useMemo } from 'react';
import type { IbhShape } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { PartShape } from '@igus/tribology-domain';
import {
  calcCircumference,
  calcIntermittentFactors,
  calcLinearVelocity,
  calcLogEffectiveIntermittentVelocity,
  calcPivotingVelocity,
  calcRotationVelocity,
  distanceImperialToMetric,
  forceImperialToMetric,
  forceMetricToImperial,
  getSurfacePressure,
  isUsedAxial,
  pickAreaCalculationFn,
} from '@igus/tribology-domain';
import { lifetimeConfigParams } from '@hub/bearing-hub/common/constants';
import type { LifetimeConfigParams, LifetimeParameters } from '@hub/bearing-hub/common/types';
import {
  DefaultServiceLifeForBearings,
  DefaultServiceLifeForBearingsForUS,
} from '@hub/bearing-hub/libs/BearingsLifetime/constants';
import {
  fromMetricPressureMega,
  fromMetricVelocityMinute,
  toMetricPressureMega,
  toMetricVelocityMinute,
  UnitSystem,
} from '@igus/kopla-domain';
import { BearingHubHelper } from '@hub/libs/bearing-hub.helper';
import { LengthCode } from '@icommerce/units';

export interface BearingDimensions {
  innerDiameter?: number;
  outerDiameter?: number;
  bearingLength?: number;
}

export interface UseLifetimeFormParams {
  shapeKey: IbhShape;
  dimensions: BearingDimensions;
  formUnitSystem: UnitSystem;
  unitSystem: UnitSystem;
  lifetimeParameters: LifetimeParameters;
  setLifetimeParametersValue: (key: LifetimeConfigParams, value: number | string | undefined) => void;
}

type VisibilityMap = {
  [K in LifetimeConfigParams]?: boolean;
};

export interface UseLifetimeFormReturn {
  getSurfaceValue: (shape: IbhShape) => number;
  getSurfacePressureValue: (shape: IbhShape, force: number) => number;
  getRotatingSpeedValue: () => number;
  getLinearSpeedValue: () => number;
  getPivotingSpeedValue: () => number;
  onLifetimeParameterValueChange: (key: LifetimeConfigParams, value: number | string | undefined) => void;
  loadInitBearingLoadValues: () => void;
  getSurfacePressureValueForDefinitions: (force: number) => number;
  visibilityMap: VisibilityMap;
  ROUNDING_PRECISION: number;
}

export const useLifetimeForm = ({
  shapeKey,
  dimensions,
  formUnitSystem,
  unitSystem,
  lifetimeParameters,
  setLifetimeParametersValue,
}: UseLifetimeFormParams): UseLifetimeFormReturn => {
  const ROUNDING_PRECISION = 100_000;

  const { innerDiameter, outerDiameter, bearingLength } = dimensions;
  const onTime = Number(lifetimeParameters.onTime);
  const offTime = Number(lifetimeParameters.offTime);
  const intermittentVFactor = onTime > 0 && offTime > 0 ? calcIntermittentFactors({ onTime, offTime }).vFactor : 1;

  const visibilityMap = useMemo<Record<string, boolean>>(
    () => ({
      [lifetimeConfigParams.surfacePressure]: !!innerDiameter && !!bearingLength,
      [lifetimeConfigParams.rotatingSpeed]: !!innerDiameter,
      [lifetimeConfigParams.pivotingSpeed]:
        !!innerDiameter && !!lifetimeParameters.pivotingAngle && !!lifetimeParameters.pivotingFrequency,
    }),
    [innerDiameter, bearingLength, lifetimeParameters.pivotingAngle, lifetimeParameters.pivotingFrequency]
  );

  const getSpeedValue = useCallback(
    (speedMetersPerHour: number) => {
      const speedMetersPerSecond = calcLogEffectiveIntermittentVelocity(
        speedMetersPerHour / 3600,
        intermittentVFactor
      ).value;
      return formUnitSystem === UnitSystem.metric
        ? speedMetersPerSecond
        : fromMetricVelocityMinute(formUnitSystem, speedMetersPerSecond * 60);
    },
    [formUnitSystem, intermittentVFactor]
  );

  const getSurfaceValue = useCallback(
    (shape: IbhShape): number => {
      const calculationFn = pickAreaCalculationFn(isUsedAxial(shape as PartShape));
      const currentUnit = unitSystem === UnitSystem.metric ? LengthCode.MILLIMETER : LengthCode.INCH;
      const convertLengthUnit = (value: number | undefined) =>
        BearingHubHelper.convertLengthUnit(
          {
            value: value ?? 0,
            unit: currentUnit,
          },
          LengthCode.MILLIMETER
        );

      return calculationFn(
        {
          bearingInnerDiameter: convertLengthUnit(innerDiameter),
          bearingOuterDiameter: convertLengthUnit(outerDiameter),
          bearingLength: convertLengthUnit(bearingLength),
        },
        UnitSystem.metric
      );
    },
    [unitSystem, innerDiameter, outerDiameter, bearingLength]
  );

  const getSurfacePressureValue = useCallback(
    (shape: IbhShape, force: number): number => {
      const forceInNewtons = formUnitSystem === UnitSystem.metric ? force : forceImperialToMetric(force);
      const pressureMega = getSurfacePressure({ force: forceInNewtons, area: getSurfaceValue(shape) });
      return fromMetricPressureMega(formUnitSystem, pressureMega);
    },
    [formUnitSystem, getSurfaceValue]
  );

  const getRotatingSpeedValue = useCallback((): number => {
    const diameter =
      unitSystem === UnitSystem.metric ? Number(innerDiameter) : distanceImperialToMetric(Number(innerDiameter));
    const circumference = calcCircumference(diameter).value;
    return getSpeedValue(calcRotationVelocity(Number(lifetimeParameters.rotationRevolutions), circumference).value);
  }, [getSpeedValue, unitSystem, innerDiameter, lifetimeParameters.rotationRevolutions]);

  const getLinearSpeedValue = useCallback((): number => {
    const strokeLength = Number(lifetimeParameters.linearStrokeLength);
    const strokeLengthMillimeters =
      formUnitSystem === UnitSystem.metric ? strokeLength : distanceImperialToMetric(strokeLength);
    const speedMetersPerHour = calcLinearVelocity(
      strokeLengthMillimeters,
      Number(lifetimeParameters.linearFrequency)
    ).value;
    return getSpeedValue(speedMetersPerHour);
  }, [getSpeedValue, formUnitSystem, lifetimeParameters.linearStrokeLength, lifetimeParameters.linearFrequency]);

  const getPivotingSpeedValue = useCallback((): number => {
    const diameter =
      unitSystem === UnitSystem.metric ? Number(innerDiameter) : distanceImperialToMetric(Number(innerDiameter));
    const circumference = calcCircumference(diameter).value;
    const speedMetersPerHour = calcPivotingVelocity(
      Number(lifetimeParameters.pivotingAngle),
      circumference,
      Number(lifetimeParameters.pivotingFrequency)
    ).value;

    return getSpeedValue(speedMetersPerHour);
  }, [
    getSpeedValue,
    unitSystem,
    lifetimeParameters.pivotingAngle,
    lifetimeParameters.pivotingFrequency,
    innerDiameter,
  ]);

  const onLifetimeParameterValueChange = useCallback(
    (key: LifetimeConfigParams, value: number | string | undefined) => {
      setLifetimeParametersValue(key, value);

      const visible: boolean = visibilityMap[key] ?? true;

      if (!visible || value === undefined) {
        return;
      }

      if (key === lifetimeConfigParams.surfacePressure) {
        const forceInNewtons = toMetricPressureMega(formUnitSystem, Number(value)) * getSurfaceValue(shapeKey);
        const force = formUnitSystem === UnitSystem.metric ? forceInNewtons : forceMetricToImperial(forceInNewtons);
        setLifetimeParametersValue(
          lifetimeConfigParams.force,
          Math.round(force * ROUNDING_PRECISION) / ROUNDING_PRECISION
        );
      }

      if (key === lifetimeConfigParams.force && visibilityMap[lifetimeConfigParams.surfacePressure]) {
        setLifetimeParametersValue(
          lifetimeConfigParams.surfacePressure,
          Math.round(getSurfacePressureValue(shapeKey, Number(value)) * ROUNDING_PRECISION) / ROUNDING_PRECISION
        );
      }

      if (key === lifetimeConfigParams.rotatingSpeed) {
        const diameter =
          unitSystem === UnitSystem.metric ? Number(innerDiameter) : distanceImperialToMetric(Number(innerDiameter));
        const speedMetersPerMinute =
          formUnitSystem === UnitSystem.metric
            ? Number(value) * 60
            : toMetricVelocityMinute(formUnitSystem, Number(value));
        setLifetimeParametersValue(
          lifetimeConfigParams.rotationRevolutions,
          Math.round(
            ((speedMetersPerMinute * intermittentVFactor * 1000) / calcCircumference(diameter).value) *
              ROUNDING_PRECISION
          ) / ROUNDING_PRECISION
        );
      }
    },
    [
      setLifetimeParametersValue,
      visibilityMap,
      getSurfaceValue,
      getSurfacePressureValue,
      shapeKey,
      formUnitSystem,
      unitSystem,
      innerDiameter,
      intermittentVFactor,
      ROUNDING_PRECISION,
    ]
  );

  const loadInitBearingLoadValues = useCallback(() => {
    if (lifetimeParameters.force == null && lifetimeParameters.surfacePressure == null) {
      const defaultForce =
        formUnitSystem === UnitSystem.metric
          ? (DefaultServiceLifeForBearings.load as { force: number }).force
          : DefaultServiceLifeForBearingsForUS.load.force;

      onLifetimeParameterValueChange(lifetimeConfigParams.force, defaultForce);
    }
  }, [lifetimeParameters.force, lifetimeParameters.surfacePressure, formUnitSystem, onLifetimeParameterValueChange]);

  const getSurfacePressureValueForDefinitions = useCallback(
    (force: number) => getSurfacePressureValue(shapeKey, force),
    [shapeKey, getSurfacePressureValue]
  );

  useEffect(() => {
    const force = Number(lifetimeParameters?.force);
    if (visibilityMap.surfacePressure && force) {
      const newSurfacePressure = getSurfacePressureValue(shapeKey, force);
      const currentSurfacePressure = lifetimeParameters.surfacePressure;

      if (currentSurfacePressure !== newSurfacePressure) {
        setLifetimeParametersValue(lifetimeConfigParams.surfacePressure, newSurfacePressure);
      }
    }
  }, [
    unitSystem,
    formUnitSystem,
    visibilityMap.surfacePressure,
    innerDiameter,
    bearingLength,
    lifetimeParameters.force,
    lifetimeParameters.surfacePressure,
  ]);

  useEffect(() => {
    const rpm = lifetimeParameters?.rotationRevolutions;
    if (rpm) {
      const newRotatingSpeed = getRotatingSpeedValue();
      const currentRotatingSpeed = lifetimeParameters.rotatingSpeed;

      if (currentRotatingSpeed !== newRotatingSpeed) {
        setLifetimeParametersValue(lifetimeConfigParams.rotatingSpeed, newRotatingSpeed);
      }
    }
  }, [
    formUnitSystem,
    unitSystem,
    innerDiameter,
    lifetimeParameters.rotationRevolutions,
    lifetimeParameters.rotatingSpeed,
    intermittentVFactor,
  ]);

  useEffect(() => {
    const newLinearSpeed = getLinearSpeedValue();
    const currentLinearSpeed = lifetimeParameters.linearSpeed;

    if (currentLinearSpeed !== newLinearSpeed) {
      setLifetimeParametersValue(lifetimeConfigParams.linearSpeed, newLinearSpeed);
    }
  }, [formUnitSystem, lifetimeParameters.linearFrequency, lifetimeParameters.linearStrokeLength, intermittentVFactor]);

  useEffect(() => {
    const newPivotingSpeed = getPivotingSpeedValue();
    const currentPivotingSpeed = lifetimeParameters.pivotingSpeed;

    if (currentPivotingSpeed !== newPivotingSpeed) {
      setLifetimeParametersValue(lifetimeConfigParams.pivotingSpeed, newPivotingSpeed);
    }
  }, [
    formUnitSystem,
    unitSystem,
    innerDiameter,
    lifetimeParameters.pivotingFrequency,
    lifetimeParameters.pivotingAngle,
    intermittentVFactor,
  ]);

  useEffect(() => {
    loadInitBearingLoadValues();
  }, [formUnitSystem, visibilityMap.surfacePressure, shapeKey]);

  return {
    getSurfaceValue,
    getSurfacePressureValue,
    getRotatingSpeedValue,
    getLinearSpeedValue,
    getPivotingSpeedValue,
    onLifetimeParameterValueChange,
    loadInitBearingLoadValues,
    getSurfacePressureValueForDefinitions,
    visibilityMap,
    ROUNDING_PRECISION,
  };
};
